import { Link } from "@remix-run/react";
import { useEffect, useId, useState } from "react";

import { useConsent } from "~/context/ConsentContext";
import { useLocalizedPath } from "~/helpers/navigationHelper";
import { useDialog } from "~/hooks/useDialog";
import common from "~/i18n/common";
import { useT } from "~/i18n/useT";

import "./CookieConsent.scss";

function Preferences({ onClose }: { onClose: () => void }) {
  const t = useT(common);
  const { consent, save, acceptAll, rejectAll } = useConsent();
  const localize = useLocalizedPath();
  const ref = useDialog(true, onClose);
  const titleId = useId();

  const [analytics, setAnalytics] = useState(consent.analytics);

  const rows = [
    {
      key: "necessary" as const,
      copy: t.consent.categories.necessary,
      checked: true,
      locked: true,
      onChange: () => {},
    },
    {
      key: "analytics" as const,
      copy: t.consent.categories.analytics,
      checked: analytics,
      locked: false,
      onChange: () => setAnalytics((v) => !v),
    },
  ];

  return (
    // See BookingModal: backdrop click closes, Escape handled by useDialog.
    <div
      className="ss-modal"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="ss-consent-prefs"
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <h2 className="ss-consent-prefs__title" id={titleId}>
          {t.consent.title}
        </h2>
        <p className="ss-consent-prefs__body">{t.consent.body}</p>

        <ul className="ss-consent-prefs__list">
          {rows.map((row) => (
            <li key={row.key} className="ss-consent-prefs__row">
              <div className="ss-consent-prefs__row-head">
                <h3>{row.copy.title}</h3>
                {row.locked ? (
                  <span className="ss-consent-prefs__always">
                    {t.consent.categories.necessary.always}
                  </span>
                ) : (
                  <label className="ss-switch">
                    <input
                      type="checkbox"
                      checked={row.checked}
                      onChange={row.onChange}
                    />
                    <span className="ss-switch__track" aria-hidden="true">
                      <span className="ss-switch__thumb" />
                    </span>
                    <span className="ss-sr-only">{row.copy.title}</span>
                  </label>
                )}
              </div>
              <p>{row.copy.body}</p>
            </li>
          ))}
        </ul>

        <div className="ss-consent-prefs__actions">
          <button
            type="button"
            className="ss-btn ss-btn--outline"
            onClick={rejectAll}
          >
            {t.consent.rejectAll}
          </button>
          <button
            type="button"
            className="ss-btn ss-btn--outline"
            onClick={() => save({ analytics })}
          >
            {t.consent.save}
          </button>
          <button
            type="button"
            className="ss-btn ss-btn--primary"
            onClick={acceptAll}
          >
            {t.consent.acceptAll}
          </button>
        </div>

        <p className="ss-consent-prefs__links">
          <Link to={localize("/privacy")}>{t.consent.privacyLink}</Link>
          <Link to={localize("/imprint")}>{t.consent.imprintLink}</Link>
        </p>
      </div>
    </div>
  );
}

export default function CookieConsent() {
  const t = useT(common);
  const {
    bannerOpen,
    preferencesOpen,
    acceptAll,
    rejectAll,
    openPreferences,
    closePreferences,
  } = useConsent();

  // Escape on the banner means "no", never "yes".
  useEffect(() => {
    if (!bannerOpen || preferencesOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") rejectAll();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [bannerOpen, preferencesOpen, rejectAll]);

  if (preferencesOpen) return <Preferences onClose={closePreferences} />;
  if (!bannerOpen) return null;

  return (
    <div
      className="ss-cookie"
      role="dialog"
      aria-live="polite"
      aria-label={t.consent.title}
    >
      <div className="ss-cookie__body">
        <h2 className="ss-cookie__title">{t.consent.title}</h2>
        <p className="ss-cookie__text">{t.consent.body}</p>
      </div>

      <div className="ss-cookie__actions">
        <button
          type="button"
          className="ss-cookie__link"
          onClick={openPreferences}
        >
          {t.consent.customise}
        </button>
        {/* Reject carries the same visual weight as Accept. Making refusal
            harder than acceptance is itself a GDPR problem (EDPB 03/2022). */}
        <button
          type="button"
          className="ss-btn ss-btn--outline ss-cookie__btn"
          onClick={rejectAll}
        >
          {t.consent.rejectAll}
        </button>
        <button
          type="button"
          className="ss-btn ss-btn--primary ss-cookie__btn"
          onClick={acceptAll}
        >
          {t.consent.acceptAll}
        </button>
      </div>
    </div>
  );
}
